import { useQuery, UseQueryResult } from '@tanstack/react-query';
import config from 'src/config';
import ApiResponse from '../ApiResponse';
import ApiError from '../ApiError';
import { SupplierDTO } from './useSupplier';

interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

interface QueryParams {
  first?: number;
  search?: string;
}

interface PaginatedApiResponse {
  edges: SupplierDTO[];
  pageInfo: PageInfo;
}

/**
 * React Query hook that fetches ALL active rebate suppliers by making a single API call.
 * TODO: Re-implement proper pagination logic to handle large datasets efficiently.
 *
 * Active rebate suppliers are those that have:
 * - Companies with rebates module enabled
 * - Existing rebate agreements AND product groups
 *
 * This is useful for components like:
 * - Constants review (processing existing rebate data)
 * - Analytics/reporting on active rebate suppliers
 *
 * For creating new rebate agreements, use the regular `useAllSuppliers` hook instead.
 *
 * @example
 * // Get all active rebate suppliers for constants review
 * const { data: activeRebateSuppliers, isLoading, error } = useAllActiveRebateSuppliers();
 *
 * @example
 * // Search across active rebate suppliers
 * const { data: filteredSuppliers } = useAllActiveRebateSuppliers({ search: 'ACME' });
 */
const useAllActiveRebateSuppliers = (
  params?: QueryParams,
  enabled = true,
): UseQueryResult<SupplierDTO[], ApiError> => {
  return useQuery<SupplierDTO[], ApiError>({
    queryKey: ['suppliers', 'active-rebates', params],
    queryFn: async (): Promise<SupplierDTO[]> => {
      const queryParams = new URLSearchParams();
      queryParams.append('first', '1000'); // Large page size to get all suppliers

      if (params?.search) {
        queryParams.append('search', params.search);
      }

      const url = `${
        config.apiBaseUrl
      }/accounts-payable/suppliers/active-rebates?${queryParams.toString()}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new Error(errorResponse.message);
      }

      const result: ApiResponse<PaginatedApiResponse> = await response.json();

      // Return all suppliers from the single page
      return result.data.edges;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

export default useAllActiveRebateSuppliers;
